import React, { useEffect, useState } from 'react';

import PartyBarList from 'lib/PartyBarList/PartyBarList';
import { Pagesection, Tabs, TabsPanel } from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';
import { PollDetails } from 'api/api.types';
import NationalCouncilExemptionBox from 'lib/NationalCouncilExemptionBox/NationalCouncilExemptionBox';
import getPrevListsNotAttending from 'components/election/utils/getPrevListsNotAttending';
import { PartyBarData } from 'lib/PartyBar/partybar.types';
import SeatsChart from './SeatsChart';
import ProportionalSeatsDistricts from './ProportionalSeatsDistricts';

type TabOptionLevel1 = 'canton' | 'districts';
type TabOptionLevel2 = 'partybars' | 'seatschart';

interface Props {
  currentYear: number;
  prevYear?: number;
  pollId: string;
  lists: PartyBarData[];
  parties: PartyBarData[];
  isBiProportional: boolean;
  previousElectionId?: string;
  previousElectionResults: PollDetails;
}

const ProportionalSeats = ({
  currentYear,
  lists,
  parties,
  prevYear,
  isBiProportional,
  pollId,
  previousElectionId,
  previousElectionResults,
}: Props) => {
  const { t } = useTranslation('wap');
  const [level1ActiveTab, setLevel1ActiveTab] = useState<TabOptionLevel1>('canton');
  const [level2ActiveTab, setLevel2ActiveTab] = useState<TabOptionLevel2>('partybars');

  useEffect(() => {
    if (level1ActiveTab === 'districts') {
      setLevel2ActiveTab('partybars');
    }
  }, [level1ActiveTab]);

  // always true since we only show seat distribution when count is finished
  const isFinal = true;

  const idsOfUsedPrevParties = parties?.map((party) => party.listId);
  const idsOfUsedPrevLists = lists?.map((list) => list.partyId);

  const idsOfUserPrev = isBiProportional ? idsOfUsedPrevLists : idsOfUsedPrevParties;

  const partyBarData = isBiProportional ? [...lists] : [...parties];

  // add listgroups or parties that won a seat in previous election and did not attend in this one
  const additionalListsFromPrev: PartyBarData[] = getPrevListsNotAttending(
    previousElectionResults,
    idsOfUserPrev,
    currentYear,
    isBiProportional,
  );

  if (additionalListsFromPrev) {
    partyBarData?.push(...additionalListsFromPrev);
  }

  const cantonTabs = (
    <>
      <Pagesection>
        <NationalCouncilExemptionBox
          currentElectionYear={currentYear}
          isBiproportional={isBiProportional}
        />
        <Tabs
          noBezel
          type="button"
          ariaLabel={
            isBiProportional
              ? t('proportional_tablist_seats_canton_level3')
              : t('proportional_tablist_seats_level2')
          }
        >
          <TabsPanel
            label={t('comparison')}
            icon="chartbar"
            onActive={() => setLevel2ActiveTab('partybars')}
            id="partybars"
          />
          <TabsPanel
            label={t('chart')}
            icon="chartpie"
            onActive={() => setLevel2ActiveTab('seatschart')}
            id="seatschart"
          />
        </Tabs>
      </Pagesection>
      <Pagesection>
        {level2ActiveTab === 'partybars' && (
          <PartyBarList
            lists={partyBarData}
            currentElectionYear={currentYear}
            prevElectionYear={prevYear}
            type="seats"
            isFinal={isFinal}
          />
        )}
        {level2ActiveTab === 'seatschart' && <SeatsChart lists={lists} />}
      </Pagesection>
    </>
  );

  if (!isBiProportional) {
    return cantonTabs;
  }

  return (
    <>
      <Pagesection>
        <Tabs noBezel ariaLabel={t('proportional_tablist_seats_level2')}>
          <TabsPanel
            label={t('canton')}
            onActive={() => setLevel1ActiveTab('canton')}
            id="canton"
          />
          <TabsPanel
            label={t('proportional_districts')}
            onActive={() => setLevel1ActiveTab('districts')}
            id="districts"
          />
        </Tabs>
      </Pagesection>
      {level1ActiveTab === 'canton' && cantonTabs}
      {level1ActiveTab === 'districts' && (
        <Pagesection additionalClassNames="proportionalelectiondetails__districts">
          <ProportionalSeatsDistricts
            lists={lists}
            pollId={pollId}
            currentElectionYear={currentYear}
            previousElectionId={previousElectionId}
            previousElectionResults={previousElectionResults}
            prevYear={prevYear}
          />
        </Pagesection>
      )}
    </>
  );
};

export default ProportionalSeats;
